import { Box, Text } from '@factorialco/deprecated-design-system'

import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { track, trackingEvents } from 'modules/core/lib/Tracking'
import i18n from 'modules/core/lib/i18n'

import { INCIDENCE_TYPES } from 'modules/payroll_bundle/lib/Incidences/Types'
import fieldTranslations from 'modules/payroll_integrations_datev_api/lib/fieldTranslations'
import type { Sync } from 'modules/payroll_integrations_datev_lauds/lib/IncidenceSync/store'
import { Status } from 'modules/payroll_integrations_datev_lauds/lib/IncidenceSync/store'

import Available from './Banners/Available'
import Blocked from './Banners/Blocked'
import Done from './Banners/Done'
import MissingEmployeeCode from './Banners/MissingEmployeeCode'
import Pending from './Banners/Pending'

interface Props {
  sync: Sync
  syncing: boolean
  onSync: () => void
  onStatusCheck: () => void
  checkingStatus: boolean
  pendingState: boolean
  setPendingState: (value: boolean) => void
  incidenceType: keyof typeof INCIDENCE_TYPES
}

const Absences = ({
  sync,
  syncing,
  onSync,
  onStatusCheck,
  checkingStatus,
  pendingState,
  setPendingState,
  incidenceType,
}: Props) => {
  const {
    access: { id: accessId },
    company: { id: companyId },
  } = useCurrentSession()

  const unavailableFields = sync.unavailable_fields ?? []

  const blockingList = ['missing_end_date', 'missing_full_day', 'missing_working_hours']

  const hasBlockedList = blockingList.some((field) => unavailableFields?.includes(field))

  const syncContent = () => {
    switch (sync.status) {
      case Status.Available:
        return (
          <Available
            onSync={onSync}
            syncing={syncing}
            title={i18n.t('payroll_integrations.incidences.datev_lauds.absences.available.title')}
            description={i18n.t(
              'payroll_integrations.incidences.datev_lauds.absences.available.message'
            )}
            submitButton={i18n.t(
              'payroll_integrations.incidences.datev_lauds.absences.available.submit'
            )}
          />
        )
      case Status.Pending:
        return (
          <Pending
            checkingStatus={checkingStatus}
            onStatusCheck={onStatusCheck}
            pendingState={pendingState}
            isLeaveIncidence
          />
        )
      case Status.Done:
        setPendingState(false)
        track(trackingEvents.payrollIntegrationsDatevLauds.datevLaudsAbsenceCreated),
          {
            company_id: companyId,
            access_id: accessId,
            type: incidenceType,
          }
        return (
          <Done
            title={i18n.t('payroll_integrations.incidences.datev_lauds.absences.done.title')}
            description={i18n.t(
              'payroll_integrations.incidences.datev_lauds.absences.done.message'
            )}
          />
        )

      case Status.Unavailable:
        return hasBlockedList ? (
          <>
            <Text.Header2>
              {i18n.t('payroll_integrations.incidences.datev_lauds.absences.unavailable.title')}
            </Text.Header2>
            {unavailableFields
              .filter((field) => blockingList.includes(field))
              .map((field: string) => (
                <Text size="300" key={field}>
                  {fieldTranslations.t(field)}
                </Text>
              ))}
          </>
        ) : (
          <MissingEmployeeCode onSync={onSync} syncing={syncing} />
        )

      case Status.Blocked:
        setPendingState(false)
        return (
          <Blocked
            sync={sync}
            onSync={onSync}
            syncing={syncing}
            submitButton={i18n.t('payroll_integrations.incidences.datev_lauds.hiring.error.retry')}
            isLeaveIncidence
          />
        )
      default:
        throw new Error(`Rendering unknown status ${sync.status}`)
    }
  }

  const background = () => {
    switch (sync.status) {
      case Status.Available:
        return 'darker080'
      case Status.Unavailable:
        return hasBlockedList ? 'champagne200' : 'radicalRed200'
      case Status.Done:
        return 'lime200'
      case Status.Pending:
        return 'viridian100'
      case Status.Blocked:
        return 'radicalRed200'
      default:
        throw new Error(`Rendering unknown status ${sync.status}`)
    }
  }

  if (
    sync.status === Status.Unavailable &&
    sync?.unavailable_fields?.every((item) =>
      [
        'missing_salary_code',
        'missing_legal_entity_mapping',
        'missing_leave_type_mapping',
      ].includes(item)
    )
  )
    return

  return (
    <Box
      background={background()}
      paddingX="s24"
      paddingY="s16"
      borderRadius={{
        all: 'abs008',
      }}
      gap="s16"
    >
      {syncContent()}
    </Box>
  )
}

export default Absences
